import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  DialogContent,
  DialogActions,
  Checkbox,
  makeStyles,
} from "@material-ui/core";

export type PayBeforePlayAddChoice = "unpaid" | "paid" | "credit";

export type AddPlayerCreditVoucher = {
  _id: string;
  sourceGameName: string;
  sourceGameCode: string;
  remainingPence: number;
  amountPence: number;
  expiresAt: string;
  applyPence: number;
};

export type AddPlayerCreditOffer = {
  feePence: number;
  applyPence: number;
  availablePence: number;
  vouchers: AddPlayerCreditVoucher[];
};

const money = (pence: number) => `£${(pence / 100).toFixed(2)}`;

const formatExpiry = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
};

const useStyles = makeStyles(theme => ({
  content: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(2),
  },
  intro: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
    fontSize: "0.875rem",
  },
  radioGroup: {
    display: "flex",
    flexDirection: "column",
  },
  optionCard: {
    margin: 0,
    alignItems: "flex-start",
    width: "100%",
    marginLeft: 0,
    marginRight: 0,
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    transition: "border-color 0.15s ease, background-color 0.15s ease",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.02)",
    },
    "&:last-child": {
      marginBottom: 0,
    },
  },
  optionLabel: {
    flex: 1,
    marginLeft: theme.spacing(0.5),
  },
  optionCardSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: "rgba(25, 118, 210, 0.06)",
  },
  labelBlock: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    paddingLeft: theme.spacing(0.5),
    width: "100%",
  },
  optionTitle: {
    fontWeight: 600,
    fontSize: "0.9375rem",
    lineHeight: 1.35,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  optionDesc: {
    fontSize: "0.8125rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.45,
    fontWeight: 400,
    display: "block",
    whiteSpace: "normal",
  },
  checkingHint: {
    fontSize: "0.8125rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  voucherList: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    marginTop: theme.spacing(1),
    gap: theme.spacing(1),
  },
  voucherRow: {
    width: "100%",
    paddingTop: theme.spacing(0.75),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(0.5),
    cursor: "pointer",
  },
  voucherCheck: {
    padding: 4,
    marginTop: -2,
  },
  voucherBody: {
    flex: 1,
    minWidth: 0,
  },
  voucherTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    width: "100%",
  },
  voucherName: {
    fontWeight: 600,
    fontSize: "0.8125rem",
    lineHeight: 1.35,
    color: theme.palette.text.primary,
  },
  voucherMeta: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
    display: "block",
  },
  voucherAmount: {
    fontWeight: 600,
    fontSize: "0.8125rem",
    whiteSpace: "nowrap",
  },
  actions: {
    padding: theme.spacing(2, 3),
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  },
}));

const defaultSelectedIds = (credit?: AddPlayerCreditOffer | null) =>
  new Set((credit?.vouchers || []).filter(v => v.applyPence > 0).map(v => v._id));

const spendFromSelection = (
  vouchers: AddPlayerCreditVoucher[],
  selectedIds: Set<string>,
  feePence: number,
) => {
  const selected = vouchers
    .filter(v => selectedIds.has(v._id))
    .sort((a, b) => new Date(a.expiresAt).getTime() - new Date(b.expiresAt).getTime());
  let need = feePence;
  const spendById: Record<string, number> = {};
  let applyPence = 0;
  for (const voucher of selected) {
    const spend = need > 0 ? Math.min(voucher.remainingPence, need) : 0;
    spendById[voucher._id] = spend;
    applyPence += spend;
    need -= spend;
  }
  return { applyPence, spendById };
};

interface Props {
  onConfirm: (choice: PayBeforePlayAddChoice, creditIds?: string[]) => void;
  onCancel: () => void;
  credit?: AddPlayerCreditOffer | null;
  creditLoading?: boolean;
}

/** Organiser add-player: radio choice + confirm for pay-before-play games. */
const PayBeforePlayAddPlayerChoice: React.FC<Props> = ({ onConfirm, onCancel, credit, creditLoading }) => {
  const classes = useStyles();
  const [choice, setChoice] = useState<PayBeforePlayAddChoice>("unpaid");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  const selectChoice = (value: PayBeforePlayAddChoice) => {
    setChoice(value);
    setSelectedIds(value === "credit" ? defaultSelectedIds(credit) : new Set());
  };

  const choiceRef = useRef(choice);
  choiceRef.current = choice;
  useEffect(() => {
    setSelectedIds(choiceRef.current === "credit" ? defaultSelectedIds(credit) : new Set());
  }, [credit]);

  const hasCredit = Boolean(credit && credit.vouchers.length);
  const defaultSelection = useMemo(
    () =>
      credit
        ? spendFromSelection(credit.vouchers, defaultSelectedIds(credit), credit.feePence)
        : { applyPence: 0, spendById: {} },
    [credit],
  );
  const selection = useMemo(
    () => (credit ? spendFromSelection(credit.vouchers, selectedIds, credit.feePence) : { applyPence: 0, spendById: {} }),
    [credit, selectedIds],
  );
  const shown = choice === "credit" ? selection : defaultSelection;
  const covers = Boolean(credit && shown.applyPence >= credit.feePence);
  const leftoverPence = credit ? Math.max(0, credit.availablePence - shown.applyPence) : 0;
  const creditReady = choice !== "credit" || selection.applyPence >= 1;

  const toggleVoucher = (id: string) => {
    if (choice !== "credit") {
      selectChoice("credit");
      return;
    }
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = () => {
    if (choice === "credit") {
      if (selection.applyPence < 1) return;
      onConfirm(choice, [...selectedIds]);
      return;
    }
    onConfirm(choice);
  };

  return (
    <>
      <DialogContent className={classes.content}>
        <Typography className={classes.intro}>
          Choose how to record this player&apos;s entry fee, then tap Add player.
        </Typography>

        <RadioGroup
          className={classes.radioGroup}
          value={choice}
          onChange={(_, value) => selectChoice(value as PayBeforePlayAddChoice)}
        >
          <FormControlLabel
            value="unpaid"
            classes={{
              root: `${classes.optionCard} ${choice === "unpaid" ? classes.optionCardSelected : ""}`,
              label: classes.optionLabel,
            }}
            control={<Radio color="primary" size="small" />}
            label={
              <span className={classes.labelBlock}>
                <Typography component="span" className={classes.optionTitle}>
                  Not paid yet
                </Typography>
                <Typography component="span" className={classes.optionDesc}>
                  Add to the roster unpaid. Shows a red £ until they pay in the app or you mark them paid. Cannot play
                  until paid.
                </Typography>
              </span>
            }
          />

          <FormControlLabel
            value="paid"
            classes={{
              root: `${classes.optionCard} ${choice === "paid" ? classes.optionCardSelected : ""}`,
              label: classes.optionLabel,
            }}
            control={<Radio color="primary" size="small" />}
            label={
              <span className={classes.labelBlock}>
                <Typography component="span" className={classes.optionTitle}>
                  Already paid (cash, bank, etc.)
                </Typography>
                <Typography component="span" className={classes.optionDesc}>
                  You received payment outside the app. No red £; they can play straight away.
                </Typography>
              </span>
            }
          />

          {creditLoading ? (
            <Typography className={classes.checkingHint}>Checking for game credit…</Typography>
          ) : hasCredit && credit ? (
            <FormControlLabel
              value="credit"
              classes={{
                root: `${classes.optionCard} ${choice === "credit" ? classes.optionCardSelected : ""}`,
                label: classes.optionLabel,
              }}
              control={<Radio color="primary" size="small" />}
              label={
                <span className={classes.labelBlock}>
                  <Typography component="span" className={classes.optionTitle}>
                    Use game credit ({money(shown.applyPence)})
                  </Typography>
                  <Typography component="span" className={classes.optionDesc}>
                    {shown.applyPence < 1
                      ? `Tick at least one credit. Fee is ${money(credit.feePence)}.`
                      : covers
                      ? `${money(shown.applyPence)} covers this game. No red £. They can play straight away.`
                      : `Applies ${money(shown.applyPence)} toward the ${money(
                          credit.feePence,
                        )} fee. Records the rest as cash / other means.`}
                    {leftoverPence > 0 ? ` ${money(leftoverPence)} stays on credit for later.` : ""}
                  </Typography>
                  <span className={classes.voucherList}>
                    {credit.vouchers.map(voucher => {
                      const checked = choice === "credit" && selectedIds.has(voucher._id);
                      const spend = choice === "credit" ? selection.spendById[voucher._id] || 0 : 0;
                      const expiry = formatExpiry(voucher.expiresAt);
                      const same = voucher.remainingPence === voucher.amountPence;
                      const metaParts = [
                        voucher.sourceGameCode || null,
                        expiry ? `Expires ${expiry}` : null,
                        !same ? `${money(voucher.remainingPence)} of ${money(voucher.amountPence)}` : null,
                        checked && spend > 0 && spend < voucher.remainingPence
                          ? `${money(voucher.remainingPence - spend)} left after this add`
                          : checked && spend <= 0
                          ? "Ticked, not needed for this fee"
                          : choice === "credit" && !checked
                          ? "Not used this time"
                          : null,
                      ].filter(Boolean);
                      return (
                        <span
                          key={voucher._id}
                          className={classes.voucherRow}
                          onClick={event => {
                            event.preventDefault();
                            event.stopPropagation();
                            toggleVoucher(voucher._id);
                          }}
                          onMouseDown={event => event.preventDefault()}
                        >
                          <Checkbox
                            className={classes.voucherCheck}
                            color="secondary"
                            size="small"
                            checked={checked}
                            tabIndex={-1}
                            disableRipple
                          />
                          <span className={classes.voucherBody}>
                            <span className={classes.voucherTop}>
                              <Typography component="span" className={classes.voucherName}>
                                {voucher.sourceGameName}
                              </Typography>
                              <Typography component="span" className={classes.voucherAmount}>
                                {money(voucher.remainingPence)}
                              </Typography>
                            </span>
                            <Typography component="span" className={classes.voucherMeta}>
                              {metaParts.join(" · ")}
                            </Typography>
                          </span>
                        </span>
                      );
                    })}
                  </span>
                </span>
              }
            />
          ) : null}
        </RadioGroup>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button onClick={onCancel} color="default">
          Cancel
        </Button>
        <Button onClick={handleConfirm} color="primary" variant="contained" disabled={creditLoading || !creditReady}>
          Add player
        </Button>
      </DialogActions>
    </>
  );
};

export default PayBeforePlayAddPlayerChoice;
